import React, { useState } from 'react';
import './SellerRewards.css';
import Header from '../Header';
import Footer from '../Footer';
import {
  sellerData,
  performanceMetrics,
  nextMilestone,
  rewardCategories,
  featuredReward,
  milestones,
  trustMetrics,
  leagueCategories,
  growthStats,
  achievements,
  equipmentItems,
  walletItems,
  pointHistory,
  clubBenefits,
} from './data';


/* ───────────────────────────────────────────────────
   STATUS HERO
─────────────────────────────────────────────────── */
const StatusHero = ({ data }) => {
  const pct = Math.round((data.jpBalance / data.jpTarget) * 100);
  return (
    <div className="sr-hero">
      <div className="sr-hero-top">
        <div className="sr-hero-level">
          <div className="sr-level-badge">
            <span>💎</span> {data.level} Seller
          </div>
          <div className="sr-level-name">{data.level}</div>
          <div className="sr-level-subtitle">Seller Level · JMI Seller Rewards</div>
        </div>
        <div className="sr-hero-badge-icon">💎</div>
      </div>

      <div className="sr-hero-jp">
        <span className="sr-jp-value">{data.jpBalance.toLocaleString('en-IN')}</span>
        <span className="sr-jp-unit">JP</span>
      </div>

      <div className="sr-progress-section">
        <div className="sr-progress-labels">
          <span>{data.jpBalance.toLocaleString('en-IN')} / {data.jpTarget.toLocaleString('en-IN')} JP</span>
          <span>→ {data.nextLevel}</span>
        </div>
        <div className="sr-progress-track">
          <div className="sr-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="sr-progress-note">{data.jpToNext.toLocaleString('en-IN')} JP to {data.nextLevel}</div>
      </div>

      <div className="sr-hero-btns">
        <button className="sr-btn-primary">View Benefits</button>
        <button className="sr-btn-ghost">How Rewards Work</button>
      </div>
    </div>
  );
};

/* ───────────────────────────────────────────────────
   PERFORMANCE METRICS
─────────────────────────────────────────────────── */
const PerformanceMetrics = ({ data }) => {
  const cards = [
    { label: 'Sales', value: data.sales.value, change: data.sales.change, icon: '₹', color: 'gold' },
    { label: 'Orders Fulfilled', value: data.orders.value, change: data.orders.change, icon: '📦', color: 'blue' },
    { label: 'Growth', value: data.growth.value, change: data.growth.change, icon: '📈', color: 'green' },
    { label: 'Seller Trust', value: data.trust.value, change: data.trust.change, icon: '🛡️', color: 'purple' },
  ];
  return (
    <div>
      <div className="sr-section-label">Seller Performance</div>
      <div className="sr-perf-grid">
        {cards.map((c) => (
          <div key={c.label} className="sr-perf-card">
            <div className={`sr-perf-icon ${c.color}`}>{c.icon}</div>
            <div className="sr-perf-value">{c.value}</div>
            <div className="sr-perf-label">{c.label}</div>
            <div className="sr-perf-change up">{c.change}</div>
          </div>
        ))}
      </div>
      <div className="sr-mini-metrics" style={{ marginTop: 12 }}>
        {data.mini.map((m) => (
          <div key={m.label} className="sr-mini-chip">
            <span className="chip-dot" style={{ background: m.color }} />
            {m.label}: <strong style={{ color: 'var(--text-primary)' }}>{m.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ───────────────────────────────────────────────────
   NEXT MILESTONE
─────────────────────────────────────────────────── */
const NextMilestone = ({ data }) => {
  const pct = Math.round((data.current / data.target) * 100);
  const remaining = data.target - data.current;
  return (
    <div>
      <div className="sr-section-label">Next Seller Milestone</div>
      <div className="sr-milestone-card">
        <div className="sr-milestone-img">{data.emoji}</div>
        <div className="sr-milestone-info">
          <div className="sr-milestone-title">Next Milestone</div>
          <div className="sr-milestone-goal">{data.goal}</div>
          <div className="sr-milestone-reward">🎁 Reward: {data.reward}</div>
          <div className="sr-milestone-progress-bar">
            <div className="sr-milestone-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="sr-milestone-nums">
            <span><strong>{data.current.toLocaleString('en-IN')}</strong> / {data.target.toLocaleString('en-IN')} orders</span>
            <span><strong>{remaining}</strong> remaining</span>
          </div>
        </div>
        <button className="sr-milestone-btn">View Milestone</button>
      </div>
    </div>
  );
};

/* ───────────────────────────────────────────────────
   REWARD CATEGORIES
─────────────────────────────────────────────────── */
const RewardCategories = ({ categories, onSelectReward }) => (
  <div>
    <div className="sr-section-label">Seller Reward Categories</div>
    <div className="sr-categories">
      {categories.map((cat) => (
        <div key={cat.id} className="sr-cat-card">
          <div className="sr-cat-header">
            <div className="sr-cat-icon" style={{ background: cat.color }}>
              {cat.icon}
            </div>
            <div>
              <div className="sr-cat-title">{cat.label}</div>
              <div className="sr-cat-desc">{cat.desc}</div>
            </div>
            <div className="sr-cat-count">{cat.rewards.length} rewards</div>
          </div>
          <div className="sr-cat-rewards">
            {cat.rewards.map((r) => (
              <div
                key={r}
                className="sr-reward-chip"
                onClick={() => onSelectReward(r, cat)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onSelectReward(r, cat)}
              >
                {r}
              </div>
            ))}
          </div>
          <div className="sr-cat-footer">
            <button className="sr-cat-view-btn">View All {cat.label} Rewards →</button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ───────────────────────────────────────────────────
   REWARD DETAIL MODAL
─────────────────────────────────────────────────── */
const RewardDetailModal = ({ reward, onClose }) => {
  if (!reward) return null;
  return (
    <div className="sr-modal-overlay" onClick={onClose}>
      <div className="sr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sr-modal-handle" />
        <div className="sr-modal-img">{featuredReward.emoji}</div>
        <h2 className="sr-modal-title">{reward.name || featuredReward.title}</h2>
        <div className="sr-modal-gate-chips">
          {featuredReward.gates.map((g) => (
            <div key={g.label} className={`sr-gate-chip ${g.type}`}>
              {g.type === 'orders' ? '📦' : g.type === 'level' ? '🏅' : '✅'} {g.label}
            </div>
          ))}
        </div>
        <div className="sr-modal-includes">
          <h4>Includes</h4>
          <ul>
            {featuredReward.includes.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
        <div className="sr-value-card">
          <div className="sr-value-row">
            <span className="sr-value-label">Market Value</span>
            <span className="sr-value-amount">₹{featuredReward.marketValue.toLocaleString('en-IN')}</span>
          </div>
          <div className="sr-value-row">
            <span className="sr-value-label">JMI Support</span>
            <span className="sr-value-amount" style={{ color: 'var(--success)' }}>
              − ₹{featuredReward.jmiSupport.toLocaleString('en-IN')}
            </span>
          </div>
          <div className="sr-value-row">
            <span className="sr-value-label">Your Contribution</span>
            <span className="sr-value-amount">₹{featuredReward.sellerContribution.toLocaleString('en-IN')}</span>
          </div>
        </div>
        <button className="sr-request-btn">Request Reward</button>
      </div>
    </div>
  );
};

/* ───────────────────────────────────────────────────
   MILESTONES TIMELINE
─────────────────────────────────────────────────── */
const MilestonesTimeline = () => {
  const tabs = ['Orders', 'Products', 'Quality', 'Dispatch', 'Repeat Buyers', 'Growth'];
  const [activeTab, setActiveTab] = useState('Orders');
  const items = activeTab === 'Products' ? milestones.products : milestones.orders;
  return (
    <div>
      <div className="sr-section-label">Seller Milestones</div>
      <div className="sr-tabs">
        {tabs.map((t) => (
          <button
            key={t}
            className={`sr-tab ${activeTab === t ? 'active' : ''}`}
            onClick={() => setActiveTab(t)}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="sr-timeline">
        {items.map((item, i) => (
          <div key={i} className="sr-timeline-item">
            <div className={`sr-timeline-dot ${item.status}`}>
              {item.status === 'done' ? '✓' : item.status === 'current' ? '●' : '○'}
            </div>
            <div className="sr-timeline-body">
              <div className={`sr-timeline-label ${item.status === 'locked' ? 'locked-text' : ''}`}>
                {item.label}
              </div>
              <div className={`sr-timeline-reward ${item.status === 'locked' ? 'locked-text' : ''}`}>
                🎁 {item.reward}
              </div>
              {item.status === 'current' && item.progress && (
                <div className="sr-timeline-meta">{item.progress} / 250 orders</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ───────────────────────────────────────────────────
   TRUST DASHBOARD
─────────────────────────────────────────────────── */
const TrustDashboard = ({ metrics, score }) => (
  <div>
    <div className="sr-section-label">Quality & Trust Dashboard</div>
    <div className="sr-trust-card">
      <div className="sr-trust-header">
        <div>
          <div className="sr-trust-score-big">{score}</div>
          <div className="sr-trust-score-label">Seller Trust Score</div>
        </div>
        <div className="sr-trust-badge-eligible">✅ Eligible for Premium Rewards</div>
      </div>
      <div className="sr-trust-metrics">
        {metrics.map((m) => {
          const displayVal = m.inverse ? m.value : m.value;
          const barWidth = m.inverse ? Math.max(0, 100 - m.value * 10) : m.value;
          return (
            <div key={m.label} className="sr-trust-row">
              <span className="sr-trust-row-label">{m.label}</span>
              <div className="sr-trust-bar-track">
                <div
                  className={`sr-trust-bar-fill ${m.type}`}
                  style={{ width: `${Math.min(100, barWidth)}%` }}
                />
              </div>
              <span className="sr-trust-pct">
                {m.inverse ? `${displayVal}%` : `${displayVal}%`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

/* ───────────────────────────────────────────────────
   SELLER LEAGUE
─────────────────────────────────────────────────── */
const SellerLeague = ({ categories }) => (
  <div>
    <div className="sr-league-card">
      <div className="sr-league-title">JMI Seller League</div>
      <div className="sr-league-heading">Compete through seller performance.</div>
      <div className="sr-rank-display">
        <div className="sr-rank-number">#12</div>
        <div className="sr-rank-info">
          <div className="sr-rank-change">↑ 5 positions</div>
          <div className="sr-rank-desc">vs last month</div>
        </div>
      </div>
      <div className="sr-categories-badges">
        {categories.map((c) => (
          <div key={c.label} className={`sr-league-badge ${c.active ? 'active' : ''}`}>
            {c.label}
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ───────────────────────────────────────────────────
   GROWTH STATS
─────────────────────────────────────────────────── */
const GrowthStats = ({ stats }) => (
  <div>
    <div className="sr-section-label">Your Seller Growth</div>
    <div className="sr-growth-grid">
      {stats.map((s) => (
        <div key={s.label} className="sr-growth-item">
          <div className="sr-growth-label">{s.label}</div>
          <div className="sr-growth-value">{s.value}</div>
          <div className="sr-growth-change">{s.change}</div>
        </div>
      ))}
    </div>
  </div>
);

/* ───────────────────────────────────────────────────
   ACHIEVEMENTS
─────────────────────────────────────────────────── */
const Achievements = ({ badges }) => (
  <div>
    <div className="sr-section-label">Seller Achievements</div>
    <div className="sr-badges-grid">
      {badges.map((b) => (
        <div key={b.name} className="sr-badge-item">
          <div
            className={`sr-badge-circle ${b.earned ? 'earned' : 'locked'}`}
            style={{ background: b.bg }}
          >
            {b.emoji}
          </div>
          <div className="sr-badge-name">{b.name}</div>
        </div>
      ))}
    </div>
  </div>
);

/* ───────────────────────────────────────────────────
   EQUIPMENT SUPPORT
─────────────────────────────────────────────────── */
const EquipmentSupport = ({ items }) => (
  <div>
    <div className="sr-section-label">JMI Equipment Support</div>
    <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 14, marginTop: -4 }}>
      Use your JMI achievements to upgrade your business.
    </p>
    <div className="sr-equip-grid">
      {items.map((item) => (
        <div key={item.name} className="sr-equip-card">
          <div className="sr-equip-icon" style={{ background: item.bg }}>{item.emoji}</div>
          <div className="sr-equip-info">
            <div className="sr-equip-name">{item.name}</div>
            <div className="sr-equip-pricing">
              <span className="sr-equip-price-chip market">₹{item.market.toLocaleString('en-IN')}</span>
              <span className="sr-equip-price-chip support">JMI −₹{item.support.toLocaleString('en-IN')}</span>
              <span className="sr-equip-price-chip contribution">You ₹{item.contribution.toLocaleString('en-IN')}</span>
            </div>
          </div>
          <button className="sr-apply-btn">Apply</button>
        </div>
      ))}
    </div>
  </div>
);

/* ───────────────────────────────────────────────────
   REWARD WALLET
─────────────────────────────────────────────────── */
const RewardWallet = () => {
  const tabs = ['Available', 'Unlocked', 'Pending', 'Redeemed', 'Expired'];
  const [activeTab, setActiveTab] = useState('Available');
  const items = walletItems[activeTab.toLowerCase()] || walletItems.available;
  return (
    <div>
      <div className="sr-section-label">My Seller Rewards</div>
      <div className="sr-tabs">
        {tabs.map((t) => (
          <button
            key={t}
            className={`sr-tab ${activeTab === t ? 'active' : ''}`}
            onClick={() => setActiveTab(t)}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="sr-wallet-list">
        {items.length > 0 ? items.map((item, i) => (
          <div key={i} className="sr-wallet-item">
            <div className="sr-wallet-icon" style={{ background: 'var(--accent-gold-light)' }}>
              {item.emoji}
            </div>
            <div className="sr-wallet-info">
              <div className="sr-wallet-name">{item.name}</div>
              <div className="sr-wallet-req">{item.req}</div>
              <div className="sr-wallet-date">{item.date}</div>
            </div>
            <div className="sr-wallet-status">
              <div className={`sr-status-chip ${item.status}`}>{item.status}</div>
              {item.status === 'available' && <button className="sr-redeem-btn">Redeem</button>}
              {item.status === 'unlocked' && <button className="sr-redeem-btn">Request</button>}
            </div>
          </div>
        )) : (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.84rem' }}>
            No rewards in this category yet.
          </div>
        )}
      </div>
    </div>
  );
};

/* ───────────────────────────────────────────────────
   POINT HISTORY
─────────────────────────────────────────────────── */
const PointHistory = () => {
  const filters = ['All', 'Orders', 'Products', 'Quality', 'Dispatch', 'Growth', 'Bonuses', 'Reversals'];
  const [activeFilter, setActiveFilter] = useState('All');
  return (
    <div>
      <div className="sr-section-label">JP Point History</div>
      <div className="sr-tabs">
        {filters.map((f) => (
          <button
            key={f}
            className={`sr-tab ${activeFilter === f ? 'active' : ''}`}
            onClick={() => setActiveFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>
      <div className="sr-history-list">
        {pointHistory.map((item, i) => (
          <div key={i} className="sr-history-item">
            <div className={`sr-history-icon ${item.type}`}>{item.emoji}</div>
            <div style={{ flex: 1 }}>
              <div className="sr-history-desc">{item.desc}</div>
              <div className="sr-history-sub">{item.sub} · {item.date}</div>
            </div>
            <div className={`sr-history-jp ${item.type}`}>
              {item.jp > 0 ? '+' : ''}{item.jp} JP
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ───────────────────────────────────────────────────
   BUSINESS CLUB
─────────────────────────────────────────────────── */
const SellerClub = ({ benefits }) => (
  <div className="sr-club-card">
    <div className="sr-club-badge">
      <span>🏅</span> Premium Membership
    </div>
    <div className="sr-club-title">JMI Seller Business Club</div>
    <div className="sr-club-sub">
      The highest seller achievement — exclusive access to premium business support, equipment, events, and marketplace privileges.
    </div>
    <div className="sr-club-benefits">
      {benefits.map((b) => (
        <div key={b} className="sr-club-benefit">
          <span className="sr-club-benefit-dot" />
          {b}
        </div>
      ))}
    </div>
    <button className="sr-club-cta">Learn About Seller Business Club</button>
  </div>
);

/* ═══════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════ */
const SellerRewards = () => {
  const [selectedReward, setSelectedReward] = useState(null);

  return (
    <div className="sr-page">
      <Header title="Rewards" />

      {/* JP balance sub-bar */}
      <div className="sr-jp-subbar">
        <span className="sr-jp-subbar-label">JMI Points</span>
        <div className="sr-jp-pill">
          <span>💰</span>
          {sellerData.jpBalance.toLocaleString('en-IN')} JP
        </div>
        <span className="sr-jp-subbar-hint">Build a better catalogue. Operate better. Sell more.</span>
      </div>

      <div className="sr-content">
        {/* 1. Status Hero */}
        <StatusHero data={sellerData} />

        {/* 2. Performance Metrics */}
        <PerformanceMetrics data={performanceMetrics} />

        {/* 3. Next Milestone */}
        <NextMilestone data={nextMilestone} />

        {/* 4. Reward Categories */}
        <RewardCategories
          categories={rewardCategories}
          onSelectReward={(name) => setSelectedReward({ name })}
        />

        {/* 5. Milestones Timeline */}
        <MilestonesTimeline />

        {/* 6. Trust Dashboard */}
        <TrustDashboard metrics={trustMetrics} score={sellerData.trustScore} />

        {/* 7. Seller League */}
        <SellerLeague categories={leagueCategories} />

        {/* 8. Growth Stats */}
        <GrowthStats stats={growthStats} />

        {/* 9. Achievements */}
        <Achievements badges={achievements} />

        {/* 10. Equipment Support */}
        <EquipmentSupport items={equipmentItems} />

        {/* 11. Reward Wallet */}
        <RewardWallet />

        {/* 12. Point History */}
        <PointHistory />

        {/* 13. Business Club */}
        <SellerClub benefits={clubBenefits} />
      </div>

      {/* Reward Detail Modal */}
      {selectedReward && (
        <RewardDetailModal
          reward={selectedReward}
          onClose={() => setSelectedReward(null)}
        />
      )}

      <Footer />
    </div>
  );
};

export default SellerRewards;
